import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminApi } from '../hooks/useAdminApi';
import {
  FilePlus2, Package, MapPin, Calendar,
  ArrowRight, CheckCircle2, Clock, Truck, Loader2, ListOrdered, ScanLine
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ScanOrderModal } from '../components/dispatch/DispatchModals';

// LOGISTIQUE-PLUS-V3 - Formulaire agent de ventes (Rep) -> alimente colonne "A planifier" du SAC

const STATUS_LABELS = {
  nouvelle: { label: 'A planifier', color: 'amber', icon: Clock },
  rdv_planifie: { label: 'RDV planifie', color: 'blue', icon: Calendar },
  attribuee_repartiteur: { label: 'Attribuee', color: 'purple', icon: Truck },
  attribuee_chauffeur: { label: 'Chauffeur assigne', color: 'cyan', icon: Truck },
  en_cours: { label: 'En cours', color: 'orange', icon: Truck },
  livree: { label: 'Livree', color: 'green', icon: CheckCircle2 },
  facturee: { label: 'Facturee', color: 'emerald', icon: CheckCircle2 },
};

const emptyStop = () => ({
  client_name: '',
  address: '',
  city: '',
  postal_code: '',
  province: 'QC',
  required_date: new Date().toISOString().slice(0, 10),
  required_time: '',
  contact_name: '',
  contact_phone: '',
  notes: '',
  handling_equipment: [],  // transpalette, forklift, dock…
});

// Options equipement (traduction FR affichée / valeur stockée en anglais pour cohérence backend)
const HANDLING_OPTIONS = [
  { value: 'transpalette', label: 'Transpalette' },
  { value: 'forklift', label: 'Chariot élévateur (lift)' },
  { value: 'dock', label: 'Quai de chargement' },
  { value: 'crane', label: 'Grue' },
  { value: 'none', label: 'Aucun (à la main)' },
];

const TRANSPORT_OPTIONS = [
  { value: 'flatbed', label: 'Flat bed (plateau)' },
  { value: 'dry_van', label: 'Remorque sèche' },
  { value: 'reefer', label: 'Réfrigéré' },
  { value: 'other', label: 'Autre' },
];

const CARGO_SECURING_OPTIONS = [
  { value: 'chains', label: 'Chaînes' },
  { value: 'straps', label: 'Sangles' },
  { value: 'tarps', label: 'Toiles' },
  { value: 'corner_protectors', label: 'Coins de protection' },
  { value: 'wood_blocks', label: 'Blocs de bois' },
  { value: 'coil_stands', label: 'Coil stands' },
];

// Petit composant réutilisable pour un groupe de checkboxes (arrimage / manutention)
const CheckboxGroup = ({ label, options, values = [], onChange, testPrefix, color = 'blue' }) => (
  <div>
    <label className="text-slate-400 text-xs mb-2 block">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = values.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => {
              const next = active ? values.filter(v => v !== opt.value) : [...values, opt.value];
              onChange(next);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              active
                ? `bg-${color}-500/20 border-${color}-500 text-${color}-300`
                : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
            data-testid={`${testPrefix}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

const StopForm = ({ label, icon: Icon, color, stop, onChange, testPrefix }) => (
  <div className={`bg-slate-800/40 border border-${color}-500/20 rounded-xl p-4`}>
    <div className={`flex items-center gap-2 mb-3 text-${color}-400 font-semibold text-sm uppercase tracking-wide`}>
      <Icon size={16} /> {label}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2">
        <label className="text-slate-400 text-xs mb-1 block">Nom du client *</label>
        <input
          type="text"
          value={stop.client_name}
          onChange={e => onChange({ ...stop, client_name: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="Ex : Entrepot ABC"
          required
          data-testid={`${testPrefix}-client-name`}
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-slate-400 text-xs mb-1 block">Adresse *</label>
        <input
          type="text"
          value={stop.address}
          onChange={e => onChange({ ...stop, address: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="123 rue Principale"
          required
          data-testid={`${testPrefix}-address`}
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Ville *</label>
        <input
          type="text"
          value={stop.city}
          onChange={e => onChange({ ...stop, city: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="Montreal"
          required
          data-testid={`${testPrefix}-city`}
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Code postal</label>
        <input
          type="text"
          value={stop.postal_code}
          onChange={e => onChange({ ...stop, postal_code: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="H1A 1A1"
          data-testid={`${testPrefix}-postal-code`}
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Date demandee *</label>
        <input
          type="date"
          value={stop.required_date}
          onChange={e => onChange({ ...stop, required_date: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          required
          data-testid={`${testPrefix}-required-date`}
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Heure (optionnel)</label>
        <input
          type="time"
          value={stop.required_time}
          onChange={e => onChange({ ...stop, required_time: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          data-testid={`${testPrefix}-required-time`}
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Contact sur place</label>
        <input
          type="text"
          value={stop.contact_name}
          onChange={e => onChange({ ...stop, contact_name: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="Nom du contact"
          data-testid={`${testPrefix}-contact-name`}
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Telephone</label>
        <input
          type="tel"
          value={stop.contact_phone}
          onChange={e => onChange({ ...stop, contact_phone: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="514-555-0123"
          data-testid={`${testPrefix}-contact-phone`}
        />
      </div>
      <div className="md:col-span-2">
        <CheckboxGroup
          label="Manutention nécessaire à cet arrêt"
          options={HANDLING_OPTIONS}
          values={stop.handling_equipment || []}
          onChange={vals => onChange({ ...stop, handling_equipment: vals })}
          testPrefix={`${testPrefix}-handling`}
          color={color}
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-slate-400 text-xs mb-1 block">Notes (optionnel)</label>
        <textarea
          value={stop.notes}
          onChange={e => onChange({ ...stop, notes: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="Instructions particulieres, quai, etc."
          data-testid={`${testPrefix}-notes`}
        />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const meta = STATUS_LABELS[status] || { label: status, color: 'slate', icon: Clock };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-${meta.color}-500/10 text-${meta.color}-400 border border-${meta.color}-500/30`}>
      <Icon size={11} /> {meta.label}
    </span>
  );
};

const AdminAgentVentesPage = () => {
  const { user, token } = useAdminAuth();
  const { get, post } = useAdminApi();

  const [pickup, setPickup] = useState(emptyStop);
  const [delivery, setDelivery] = useState(emptyStop);
  const [priority, setPriority] = useState('normal');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Transport / cargaison
  const [transportType, setTransportType] = useState('');
  const [cargoSecuring, setCargoSecuring] = useState([]);
  const [totalWeightKg, setTotalWeightKg] = useState('');
  const [totalPieces, setTotalPieces] = useState('');
  const [dimensions, setDimensions] = useState('');

  // Scanner (extraction IA d'un ordre de transport image/PDF)
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const [myOrders, setMyOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const fetchMyOrders = useCallback(async () => {
    const res = await get('/dispatch/orders');
    if (res.success) {
      const mine = (res.data.orders || res.data || [])
        .filter(o => o.created_by === user?.id)
        .slice(0, 20);
      setMyOrders(mine);
    }
    setLoadingOrders(false);
  }, [get, user?.id]);

  useEffect(() => {
    fetchMyOrders();
  }, [fetchMyOrders]);

  const resetForm = () => {
    setPickup(emptyStop());
    setDelivery(emptyStop());
    setPriority('normal');
    setOrderNotes('');
    setTransportType('');
    setCargoSecuring([]);
    setTotalWeightKg('');
    setTotalPieces('');
    setDimensions('');
  };

  const validate = () => {
    for (const s of [pickup, delivery]) {
      if (!s.client_name?.trim() || !s.address?.trim() || !s.city?.trim() || !s.required_date) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Veuillez remplir les champs obligatoires (client, adresse, ville, date) pour la cueillette ET la livraison');
      return;
    }
    setSubmitting(true);
    const payload = {
      pickup: { ...pickup, items: [] },
      delivery: { ...delivery, items: [] },
      priority,
      order_notes: orderNotes || null,
      transport_type: transportType || null,
      cargo_securing: cargoSecuring,
      total_weight_kg: totalWeightKg ? parseFloat(totalWeightKg) : null,
      total_pieces: totalPieces ? parseInt(totalPieces, 10) : null,
      dimensions: dimensions || null,
    };
    const res = await post('/dispatch/orders', payload);
    if (res.success) {
      toast.success(`Commande ${res.data.order?.order_number || 'creee'} envoyee au service client`);
      resetForm();
      fetchMyOrders();
    } else {
      toast.error(res.error || 'Erreur lors de la creation');
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-agent-ventes-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/10 rounded-xl">
            <FilePlus2 className="text-blue-400" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Nouvelle commande</h1>
            <p className="text-slate-400 text-sm">
              Saisis les infos de cueillette et de livraison - la commande sera envoyee au service client pour planification
            </p>
          </div>
        </div>
        {/* Bouton Scanner — extraction auto d'un ordre de transport reçu par email */}
        <button
          type="button"
          onClick={() => setShowScanner(true)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg font-semibold hover:bg-slate-700 hover:text-white transition-colors"
          data-testid="scan-order-btn"
        >
          <ScanLine className="w-5 h-5 text-amber-500" />
          Scanner un document
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="agent-ventes-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StopForm
            label="Cueillette"
            icon={Package}
            color="amber"
            stop={pickup}
            onChange={setPickup}
            testPrefix="pickup"
          />
          <StopForm
            label="Livraison"
            icon={MapPin}
            color="green"
            stop={delivery}
            onChange={setDelivery}
            testPrefix="delivery"
          />
        </div>

        {/* Section Transport & Cargaison — informations que le chauffeur doit préparer */}
        <div className="bg-slate-800/40 border border-orange-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3 text-orange-400 font-semibold text-sm uppercase tracking-wide">
            <Truck size={16} /> Transport & Cargaison
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Type de transport</label>
              <select
                value={transportType}
                onChange={e => setTransportType(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-orange-500 focus:outline-none"
                data-testid="transport-type"
              >
                <option value="">— Sélectionner —</option>
                {TRANSPORT_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Poids total (kg)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={totalWeightKg}
                  onChange={e => setTotalWeightKg(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-orange-500 focus:outline-none"
                  placeholder="1200"
                  data-testid="transport-weight"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Pièces</label>
                <input
                  type="number"
                  min="0"
                  value={totalPieces}
                  onChange={e => setTotalPieces(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-orange-500 focus:outline-none"
                  placeholder="12"
                  data-testid="transport-pieces"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Dimensions</label>
                <input
                  type="text"
                  value={dimensions}
                  onChange={e => setDimensions(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-orange-500 focus:outline-none"
                  placeholder="48x40x60"
                  data-testid="transport-dimensions"
                />
              </div>
            </div>
          </div>

          <CheckboxGroup
            label="Équipement d'arrimage nécessaire"
            options={CARGO_SECURING_OPTIONS}
            values={cargoSecuring}
            onChange={setCargoSecuring}
            testPrefix="cargo-securing"
            color="orange"
          />
        </div>

        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Priorite</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm"
                data-testid="agent-ventes-priority"
              >
                <option value="low">Basse</option>
                <option value="normal">Normale</option>
                <option value="high">Elevee</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-slate-400 text-xs mb-1 block">Notes de commande (optionnel)</label>
              <input
                type="text"
                value={orderNotes}
                onChange={e => setOrderNotes(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm"
                placeholder="Info generale sur la commande"
                data-testid="agent-ventes-order-notes"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={resetForm}
            disabled={submitting}
            data-testid="agent-ventes-reset"
            className="border-slate-700"
          >
            Effacer
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            data-testid="agent-ventes-submit"
          >
            {submitting ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> Envoi...</>
            ) : (
              <>Envoyer au service client <ArrowRight size={16} className="ml-2" /></>
            )}
          </Button>
        </div>
      </form>

      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <ListOrdered size={18} className="text-slate-400" />
          <h2 className="text-lg font-semibold text-white">Mes commandes recentes</h2>
        </div>
        {loadingOrders ? (
          <div className="text-slate-500 text-center py-8">Chargement...</div>
        ) : myOrders.length === 0 ? (
          <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-8 text-center text-slate-500 text-sm">
            Aucune commande envoyee pour le moment. Cree ta premiere ci-dessus.
          </div>
        ) : (
          <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2">Numero</th>
                  <th className="text-left px-4 py-2">Statut</th>
                  <th className="text-left px-4 py-2">Priorite</th>
                  <th className="text-left px-4 py-2">Repartiteur</th>
                  <th className="text-left px-4 py-2">Cree le</th>
                </tr>
              </thead>
              <tbody>
                {myOrders.map(o => (
                  <tr key={o.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`agent-ventes-order-${o.id}`}>
                    <td className="px-4 py-2 text-white font-mono text-xs">{o.order_number}</td>
                    <td className="px-4 py-2"><StatusBadge status={o.workflow_status || 'nouvelle'} /></td>
                    <td className="px-4 py-2 text-slate-400 capitalize">{o.priority || 'normal'}</td>
                    <td className="px-4 py-2 text-slate-400">{o.assigned_dispatcher_name || '-'}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">
                      {o.created_at ? new Date(o.created_at).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scanner Modal — extraction IA d'un ordre de transport (PDF/image) */}
      {showScanner && (
        <ScanOrderModal
          token={token}
          scanning={scanning}
          setScanning={setScanning}
          scanResult={scanResult}
          setScanResult={setScanResult}
          onClose={() => { setShowScanner(false); setScanResult(null); }}
          onCreated={() => {
            fetchMyOrders();
            setShowScanner(false);
            setScanResult(null);
            toast.success('Commande(s) créée(s) depuis le scan');
          }}
        />
      )}
    </div>
  );
};

export default AdminAgentVentesPage;
